import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import User from 'models/response/user.interface';
import isImportantAuthRoute from 'utils/isImportantAuthRoute';

/**
 * On navigation to important app pages, refetch GET /user.
 * If the user is deactivated (is_active === false), force logout.
 */
const useActiveUserSessionCheck = (
  isAuthenticated: boolean,
  user: User | null | undefined,
  onLogout: () => void,
) => {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const previousPathnameReference = useRef(pathname);

  useEffect(() => {
    const previousPathname = previousPathnameReference.current;
    previousPathnameReference.current = pathname;

    if (previousPathname === pathname) return;
    if (!isAuthenticated) return;
    if (!isImportantAuthRoute(pathname)) return;

    queryClient.invalidateQueries(['user']);
  }, [pathname, isAuthenticated, queryClient]);

  useEffect(() => {
    if (user?.is_active === false) {
      onLogout();
    }
  }, [user, onLogout]);
};

export default useActiveUserSessionCheck;
